<template>
  <MainLayout>
    <news-header>
      <template #user>
        <UserWidget
            v-if="currentNativeLanguage && currentLearnedLanguage"
            :source-lang="currentNativeLanguage"
            :target-lang="currentLearnedLanguage"/>
      </template>
    </news-header>

    <news-menu :current-category-id="state.currentCategory?.id || categoryId"/>

    <transition name="fade" mode="out-in" :duration="{ enter: 250, leave: 80 }">
      <ContentArea
          v-if="currentNativeLanguage && currentLearnedLanguage"
          :category-title="state.currentCategory ? localize(state.currentCategory.name) : undefined"
          @collectionClick="() => viewModel.navigateToWordCollection(currentNativeLanguage!, currentLearnedLanguage!)"
          @repeatClick="() => viewModel.navigateToRepeat(currentNativeLanguage!, currentLearnedLanguage!)"
          @dailyClick="navigateToDailyWords">
        <template #headerTranslit v-if="showHeaderTranslitToggle">
          <button
            type="button"
            class="pill-btn"
            :class="{ active: globalTransliterateEnabled }"
            @click="toggleGlobalTransliteration"
          >
            <img :src="translateIconUrl" alt="" width="18" height="18" class="translit-icon" />
            {{ $t('article.translitWords') }}
          </button>
        </template>
        <template #toolbar>
          <ToolbarWidget>
            <template #toggle v-if="enableAudio">
              <div class="toolbar-toggle-group">
                <SoundToggle v-if="enableAudio" v-model="soundModel"/>
              </div>
            </template>
            <template #stats>
              <WordStatsWidget
                  @dailyClick="navigateToDailyWords"
                  @collectionClick="() => viewModel.navigateToWordCollection(currentNativeLanguage!, currentLearnedLanguage!)"
                  @repeatClick="() => viewModel.navigateToRepeat(currentNativeLanguage!, currentLearnedLanguage!)"
              />
            </template>
          </ToolbarWidget>
        </template>
        <div class="news-feed">
          <transition name="fade">
            <div
                v-if="state.showProgressBanner && state.articles.length > 0"
                class="sync-progress-banner"
            >
              <ProgressTracker
                  variant="compact"
                  :progress-items="state.progressItems"
                  :title="$t('newsList.fetchingTitle')"
                  :show-last-update="true"
              />
            </div>
          </transition>

          <transition name="feed-fade">
            <NewsListSkeleton v-if="state.showInitialSpinner" key="skeleton" />

            <div v-else class="news-content" key="content">
              <!-- Fetching state: shown when list is empty and we're syncing -->
              <div
                  v-if="state.articles.length === 0 && state.isSyncing"
                  class="fetching-state"
              >
                <div class="fetching-spinner"></div>
                <h3>{{ $t('newsList.fetchingState.title') }}</h3>
                <p>{{ $t('newsList.fetchingState.line1') }}</p>
                <p class="fetching-explanation">{{ $t('newsList.fetchingState.line2') }}</p>
                <p class="fetching-hint">{{ $t('newsList.fetchingState.line3') }}</p>
                <div v-if="state.currentOperation" class="fetching-operation">
                  {{ state.currentOperation }}
                </div>
              </div>

              <InfiniteScroll
                  v-if="currentNativeLanguage"
                  class="news-list"
                  :is-loading="state.isLoadingMore"
                  :has-more="state.hasMoreArticles"
                  :is-empty="state.articles.length === 0 && !state.isSyncing"
                  @load-more="() => viewModel.loadMoreArticles(currentNativeLanguage!, currentLearnedLanguage!, state.currentCategory?.id || '', currentMode)"
              >
                <template #default>
                  <div style="width: 100%; min-height: 600px;">
                    <news-item v-for="article in state.articles"
                               :key="article.translationId" class="article"
                               :link="article.translationId"
                               :image-url="article.imageUrl || ''"
                               :transliterate="globalTransliterateEnabled"
                               :sound-enabled="enableAudio && soundEnabled"
                               :titleData="article.title"
                               :mode="currentMode"
                               :source-lang="article.sourceLang"
                               :target-lang="article.targetLang"
                               :source="article.category || 'News'"
                               :date="article.date"
                               @open="(articleId) => viewModel.openArticle(articleId, currentNativeLanguage!, currentLearnedLanguage!, state.currentCategory?.id || '')"
                               @audio-auth-required="handleAudioAuthRequired"
                               @audio-paywall-required="handleAudioPaywallRequired"
                    />
                    <WordDetailsView/>
                  </div>
                </template>

                <template #empty>
                  <div class="empty-state">
                    <p>{{ $t('newsList.emptyState.title') }}</p>
                    <p>{{ $t('newsList.emptyState.subtitle') }}</p>
                  </div>
                </template>

                <template #end>
                  <div class="end-state">
                  </div>
                </template>
              </InfiniteScroll>
            </div>
          </transition>
        </div>
      </ContentArea>
    </transition>
  </MainLayout>
</template>

<script setup lang="ts">
import { computed, watch, onUnmounted, ref, onMounted } from "vue";
import { useRouter } from 'vue-router';
import { ISyncApiService, Language, NewsListViewModel } from "@lingvo-news/contracts";
import NewsHeader from "../../components/header/news-header.vue";
import NewsMenu from "../../components/header/news-menu.vue";
import NewsItem from "../../components/items/news-item.vue";
import NewsListSkeleton from "../../components/skeleton/NewsListSkeleton.vue";
import WordDetailsView from "../../components/WordDetailsView.vue";
import MainLayout from "../../components/MainLayout.vue";
import ContentArea from "../../components/ContentArea.vue";
import UserWidget from "../../components/header/UserWidget.vue";
import InfiniteScroll from "../../components/widgets/InfiniteScroll.vue";
import ProgressTracker from "../../components/widgets/ProgressTracker.vue";
import WordStatsWidget from "../../components/WordStatsWidget.vue";
import { useFontsLoadComplete } from "../../../composables/useLoadComplete";
import { useCurrentPreferences } from "../../../composables/useCurrentPreferences";
import { getVueEnv } from '../../../infrastructure/vue-env';
import { getCurrentUserWrapper } from '../../../infrastructure/current-user-wrapper';
import { localize } from '../../../utils/localize';
import ToolbarWidget from "../../components/widgets/ToolbarWidget.vue";
import SoundToggle from "../../components/widgets/SoundToggle.vue";

import { getCurrentUserStore } from '../../../store/services';
import { PaywallPopupViewModel } from '@lingvo-news/contracts';
import { getWordAudioStatus, isWordAudioEnabled, setWordAudioEnabled } from '../../services/word-audio-service';
import { isTelegramMiniApp } from '../../../infrastructure/auth/isTelegramMiniApp';
import { enableAudio } from '../../../constants';
import translateIconUrl from '../../../assets/translate.svg';

const {
  nativeLanguage: currentNativeLanguage,
  learnedLanguage: currentLearnedLanguage,
  mode: currentMode,
  categoryId
} = useCurrentPreferences();

if (!currentNativeLanguage.value || !currentLearnedLanguage.value) {
  throw new Error('Source and Target languages must be defined');
}

// Create ViewModel factory function
function createViewModel() {
  const env = getVueEnv();
  const currentUser = getCurrentUserWrapper();
  const syncApiService = env.get(ISyncApiService);

  return new NewsListViewModel(
      env,
      currentUser,
      syncApiService
  );
}

const TRANSLITERATE_STORAGE_KEY = 'lingvo-news-article-transliterate';

function loadTransliterateFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage?.getItem(TRANSLITERATE_STORAGE_KEY) === '1';
}

function saveTransliterateToStorage(value: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage?.setItem(TRANSLITERATE_STORAGE_KEY, value ? '1' : '0');
}

// Create ViewModel instance
const viewModel = createViewModel();
const state = viewModel.state;
const env = getVueEnv();

const globalTransliterateEnabled = ref<boolean>(loadTransliterateFromStorage());

function toggleGlobalTransliteration(): void {
  globalTransliterateEnabled.value = !globalTransliterateEnabled.value;
  saveTransliterateToStorage(globalTransliterateEnabled.value);
  state.transliterate = globalTransliterateEnabled.value;
}

const showHeaderTranslitToggle = computed<boolean>(() => {
  return currentLearnedLanguage.value === Language.HY;
});

onMounted(() => {
  state.transliterate = loadTransliterateFromStorage();
});
const router = useRouter();
const soundEnabled = ref<boolean>(isWordAudioEnabled());

const soundModel = computed<boolean>({
  get: () => soundEnabled.value,
  set: (value) => {
    void handleSoundToggle(value);
  }
});

const setSoundState = (enabled: boolean): void => {
  soundEnabled.value = enabled;
  setWordAudioEnabled(enabled);
};

const showAudioPaywall = async (): Promise<void> => {
  await env.popupService.showPopup(new PaywallPopupViewModel({
      message: { stringId: 'errors.paywall.membershipRequired', formatParams: {} },
      buyMeCoffeeUrl: 'https://buymeacoffee.com/lingvo.news/membership',
      useTelegramPayment: isTelegramMiniApp(),
  }));
};

const handleSoundToggle = async (nextValue: boolean): Promise<void> => {
  if (nextValue===soundEnabled.value) {
    return;
  }

  if (!nextValue) {
    setSoundState(false);
    return;
  }

  const userStore = getCurrentUserStore();
  await userStore.initialize();
  if (!userStore.getCurrentUserId()) {
    await env.routing.navigate(env.routing.getLoginLink());
    return;
  }

  const status = await getWordAudioStatus();
  if (status.kind==='unauthorized') {
    await env.routing.navigate(env.routing.getLoginLink());
    return;
  }

  if (status.kind==='paywall') {
    setSoundState(false);
    await showAudioPaywall();
    return;
  }

  setSoundState(true);
};

const handleAudioAuthRequired = async (): Promise<void> => {
  setSoundState(false);
  await env.routing.navigate(env.routing.getLoginLink());
};

const handleAudioPaywallRequired = async (): Promise<void> => {
  setSoundState(false);
  await showAudioPaywall();
};

const navigateToDailyWords = async (): Promise<void> => {
  if (!currentNativeLanguage.value || !currentLearnedLanguage.value) {
    return;
  }
  await router.push({
    name: 'daily-words',
    params: {
      nativeLanguage: currentNativeLanguage.value,
      learnedLanguage: currentLearnedLanguage.value,
      mode: currentMode.value
    }
  });
};

// Initialize on font load
useFontsLoadComplete(async () => {
  await viewModel.init(
      currentNativeLanguage.value!,
      currentLearnedLanguage.value!,
      categoryId.value!,
      currentMode.value
  );
});

// Watch for preference changes
watch([currentNativeLanguage, currentLearnedLanguage, currentMode], async () => {
  await viewModel.init(currentNativeLanguage.value!, currentLearnedLanguage.value!, categoryId.value!, currentMode.value);
});

// Watch for categoryId changes
watch(categoryId, async (newCategoryId) => {
  if (newCategoryId && state.categories.length > 0) {
    viewModel.setCurrentCategoryById(newCategoryId);
    if (state.currentCategory) {
      await viewModel.loadArticles(
          currentNativeLanguage.value!,
          currentLearnedLanguage.value!,
          state.currentCategory.id,
          currentMode.value
      );
    }
  }
});

// Clean up polling when component is unmounted (user navigates away)
onUnmounted(() => {
  viewModel.dispose();
});
</script>

<style scoped>
/* Page-level fade (ContentArea enter/leave) */
.fade-enter-active { transition: opacity 0.25s ease; }
.fade-leave-active { transition: opacity 0.08s ease; }
.fade-enter-from,
.fade-leave-to { opacity: 0; }

/* Feed-level fade: skeleton ↔ real content */
.feed-fade-enter-active { transition: opacity 0.3s ease; }
.feed-fade-leave-active {
  transition: opacity 0.12s ease;
  position: absolute;
  top: 0; left: 0; right: 0;
  pointer-events: none;
}
.feed-fade-enter-from,
.feed-fade-leave-to { opacity: 0; }

.news-feed {
  color: black;
  border-radius: 4px;
  padding-bottom: 30px;
  position: relative; /* required for feed-fade absolute leave */
}

.toolbar-toggle-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.translit-icon {
  flex-shrink: 0;
  opacity: 0.7;
}

.pill-btn.active .translit-icon {
  opacity: 1;
}

.article {
  margin: 20px 0 40px 0;
}

.sync-progress-banner {
  position: sticky;
  top: 0;
  z-index: 2;
  margin-bottom: 12px;
}

.news-list {
  width: 100%;
}

.empty-state,
.end-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.empty-state p,
.end-state p {
  margin: 10px 0;
  font-size: 1rem;
}

.end-state p:last-child {
  font-size: 0.9rem;
  color: #888;
}

.fetching-state {
  text-align: center;
  padding: 60px 20px;
  color: #333;
  max-width: 500px;
  margin: 0 auto;
}

.fetching-state h3 {
  margin: 24px 0 16px;
  font-size: 1.25rem;
  font-weight: 600;
  color: #222;
}

.fetching-state p {
  margin: 8px 0;
  font-size: 1rem;
  line-height: 1.5;
}

.fetching-state .fetching-explanation {
  color: #555;
  font-size: 0.95rem;
}

.fetching-state .fetching-hint {
  color: #777;
  font-size: 0.9rem;
  font-style: italic;
  margin-top: 16px;
}

.fetching-spinner {
  width: 56px;
  height: 56px;
  border: 5px solid rgba(102, 126, 234, 0.2);
  border-top: 5px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.fetching-operation {
  margin-top: 20px;
  padding: 12px 20px;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8edff 100%);
  border-radius: 8px;
  font-size: 0.9rem;
  color: #4a5568;
  font-family: monospace;
}

@media (max-width: 768px) {
  .article {
    margin: 15px 0 30px 0;
  }

  .sync-progress-banner {
    margin-bottom: 10px;
  }

  .fetching-state {
    padding: 40px 16px;
  }

  .fetching-state h3 {
    font-size: 1.1rem;
  }

  .fetching-state p {
    font-size: 0.95rem;
  }

  .fetching-spinner {
    width: 48px;
    height: 48px;
    border-width: 4px;
  }
}
</style>
